import { LitElement, html } from '../../vendor/lit/lit-core.esm.js?v=20260730a';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { apiJson } from '../utils/api_utils.js?v=20260730a';
import '../ui/lit/ks-modal.js?v=20260817b';

const COLOR_FIELDS = [
  ['ownBackground', 'Eigene Nachrichten · Hintergrund'],
  ['ownText', 'Eigene Nachrichten · Text'],
  ['receivedBackground', 'Partnernachrichten · Hintergrund'],
  ['receivedText', 'Partnernachrichten · Text'],
  ['contextBackground', 'Kontext · Hintergrund'],
  ['contextText', 'Kontext · Text'],
  ['contextBorder', 'Kontext · Rahmen'],
  ['openedCheck', 'Öffnungshaken'],
  ['activeContact', 'Aktiver Kontakt']
];

class KsMessageSettingsAdminModal extends LitElement {
  static properties = {
    presentation: { attribute: false },
    providers: { attribute: false },
    loading: { type: Boolean },
    saving: { type: Boolean },
    message: { attribute: false },
    messageError: { type: Boolean }
  };

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.presentation = null;
    this.providers = [];
    this.loading = true;
    this.saving = false;
    this.message = '';
    this.messageError = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
  }

  async _load() {
    this.loading = true;
    try {
      const payload = await apiJson('/api/admin/message-settings');
      this.presentation = payload.presentation || null;
      this.providers = Array.isArray(payload.providers) ? payload.providers : [];
      this.message = '';
      this.messageError = false;
    } catch (error) {
      this.message = error.message || 'Nachrichteneinstellungen konnten nicht geladen werden.';
      this.messageError = true;
    } finally {
      this.loading = false;
    }
  }

  _close = () => this.remove();

  _setPresentation(key, value) {
    const numericKeys = new Set(['bubbleRadius', 'bubbleMaxWidth', 'messageGap']);
    this.presentation = {
      ...this.presentation,
      [key]: numericKeys.has(key) ? Number(value) : value
    };
  }

  _setProvider(key, property, value) {
    this.providers = this.providers.map(provider => provider.providerKey === key
      ? { ...provider, [property]: property === 'sortOrder' ? Number(value) : value }
      : provider);
  }

  _moveProvider(key, direction) {
    const sorted = [...this.providers].sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    const index = sorted.findIndex(provider => provider.providerKey === key);
    const swap = index + direction;
    if (index < 0 || swap < 0 || swap >= sorted.length) return;
    [sorted[index], sorted[swap]] = [sorted[swap], sorted[index]];
    this.providers = sorted.map((provider, order) => ({ ...provider, sortOrder: (order + 1) * 10 }));
  }

  async _save() {
    if (!this.presentation || this.saving) return;
    this.saving = true;
    try {
      const payload = await apiJson('/api/admin/message-settings', {
        method: 'PUT',
        body: JSON.stringify({ presentation: this.presentation, providers: this.providers })
      });
      this.presentation = payload.presentation;
      this.providers = payload.providers || [];
      this.message = t('messages.settings.saved', 'Nachrichteneinstellungen wurden gespeichert.');
      this.messageError = false;
    } catch (error) {
      this.message = error.message || 'Nachrichteneinstellungen konnten nicht gespeichert werden.';
      this.messageError = true;
    } finally {
      this.saving = false;
    }
  }

  async _reset() {
    if (this.saving) return;
    this.saving = true;
    try {
      const payload = await apiJson('/api/admin/message-settings/reset', { method: 'POST', body: JSON.stringify({}) });
      this.presentation = payload.presentation;
      this.providers = payload.providers || [];
      this.message = t('messages.settings.reset_done', 'Standarddarstellung wurde wiederhergestellt.');
      this.messageError = false;
    } catch (error) {
      this.message = error.message || 'Standarddarstellung konnte nicht wiederhergestellt werden.';
      this.messageError = true;
    } finally {
      this.saving = false;
    }
  }

  _previewStyle() {
    const value = this.presentation || {};
    return [
      `--ks-msg-own-bg:${value.ownBackground || '#FFE2BF'}`,
      `--ks-msg-own-text:${value.ownText || '#1F1F1F'}`,
      `--ks-msg-received-bg:${value.receivedBackground || '#ECEFF2'}`,
      `--ks-msg-received-text:${value.receivedText || '#1F1F1F'}`,
      `--ks-msg-context-bg:${value.contextBackground || '#FFF8EF'}`,
      `--ks-msg-context-text:${value.contextText || '#5D3511'}`,
      `--ks-msg-context-border:${value.contextBorder || '#E7A963'}`,
      `--ks-msg-check:${value.openedCheck || '#5D3511'}`,
      `--ks-msg-radius:${Number(value.bubbleRadius) || 16}px`,
      `--ks-msg-max-width:${Number(value.bubbleMaxWidth) || 72}%`,
      `--ks-msg-gap:${Number(value.messageGap) || 8}px`
    ].join(';');
  }

  _renderBody() {
    if (this.loading) return html`<div class="empty">${t('messages.state.loading', 'Wird geladen…')}</div>`;
    if (!this.presentation) return html`<div class="empty">${this.message || 'Keine Einstellungen verfügbar.'}</div>`;
    const sortedProviders = [...this.providers].sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
    return html`
      <div class="ks-message-settings-grid">
        <section class="ks-message-settings-section">
          <h3>${t('messages.context.available', 'Verfügbare Kontexte')}</h3>
          <p class="small-text">${t('messages.settings.providers_hint', 'Provider können für neue Nachrichten aktiviert, sortiert und mit einem zugelassenen Icon versehen werden.')}</p>
          <div class="ks-message-settings-providers">
            ${sortedProviders.map((provider, index) => html`
              <div class="ks-message-settings-provider">
                <label class="admin-inline"><input type="checkbox" .checked=${provider.enabled === true} @change=${event => this._setProvider(provider.providerKey, 'enabled', event.currentTarget.checked)}> ${t(provider.labelI18nKey, provider.providerKey)}</label>
                <select aria-label="Icon" .value=${provider.iconKey} @change=${event => this._setProvider(provider.providerKey, 'iconKey', event.currentTarget.value)}>
                  <option value="briefcase">▣ Auftrag</option>
                  <option value="folder">▰ Projekt</option>
                </select>
                <button class="btn" type="button" ?disabled=${index === 0} @click=${() => this._moveProvider(provider.providerKey, -1)} aria-label="Nach oben">↑</button>
                <button class="btn" type="button" ?disabled=${index === sortedProviders.length - 1} @click=${() => this._moveProvider(provider.providerKey, 1)} aria-label="Nach unten">↓</button>
              </div>
            `)}
          </div>
        </section>
        <section class="ks-message-settings-section">
          <h3>${t('messages.settings.presentation', 'Darstellung der Chatansicht')}</h3>
          <div class="ks-message-settings-colors">
            ${COLOR_FIELDS.map(([key, label]) => html`
              <label>${t(`messages.settings.${key}`, label)}<span><input type="color" .value=${this.presentation[key]} @input=${event => this._setPresentation(key, event.currentTarget.value)}><input type="text" pattern="#[0-9A-Fa-f]{6}" maxlength="7" .value=${this.presentation[key]} @change=${event => this._setPresentation(key, event.currentTarget.value)}></span></label>
            `)}
          </div>
          <div class="ks-message-settings-ranges">
            <label>${t('messages.settings.radius', 'Rundung der Nachrichtenblasen')} <strong>${this.presentation.bubbleRadius}px</strong><input type="range" min="0" max="30" step="1" .value=${String(this.presentation.bubbleRadius)} @input=${event => this._setPresentation('bubbleRadius', event.currentTarget.value)}></label>
            <label>${t('messages.settings.width', 'Maximale Nachrichtenbreite')} <strong>${this.presentation.bubbleMaxWidth}%</strong><input type="range" min="45" max="90" step="1" .value=${String(this.presentation.bubbleMaxWidth)} @input=${event => this._setPresentation('bubbleMaxWidth', event.currentTarget.value)}></label>
            <label>${t('messages.settings.gap', 'Abstand zwischen Nachrichten')} <strong>${this.presentation.messageGap}px</strong><input type="range" min="0" max="24" step="1" .value=${String(this.presentation.messageGap)} @input=${event => this._setPresentation('messageGap', event.currentTarget.value)}></label>
          </div>
        </section>
        <section class="ks-message-settings-section ks-message-settings-preview" style=${this._previewStyle()}>
          <h3>${t('messages.settings.preview', 'Live-Vorschau')}</h3>
          <div class="ks-message-thread">
            <article class="ks-message-row is-received"><div class="ks-message-bubble"><div class="ks-message-parts">${t('messages.settings.preview_received', 'Bitte kontrolliere den Auftrag.')}</div><footer>09:41</footer></div></article>
            <article class="ks-message-row is-own"><div class="ks-message-bubble"><div class="ks-message-parts">${t('messages.settings.preview_own', 'Gerne, ich prüfe ')}<button class="ks-message-context-chip" type="button"><span>▣</span><span><strong>4711</strong><small>Eingangstor</small></span></button></div><footer>09:42 <span class="ks-message-opened">✓</span></footer></div></article>
          </div>
        </section>
      </div>
    `;
  }

  render() {
    return html`
      <ks-modal
        .modalTitle=${t('messages.settings.title', 'Nachrichteneinstellungen')}
        .subtitle=${t('messages.settings.subtitle', 'Kontexte und Darstellung der privaten 1:1-Nachrichten')}
        .closeLabel=${t('modal.gemeinsam.button.schliessen', 'Schliessen')}
        .modalClass=${'ks-message-settings-modal'}
        .message=${this.message}
        .messageError=${this.messageError}
        .bodyTemplate=${this._renderBody()}
        .extraActionsTemplate=${html`
          <button class="btn" type="button" ?disabled=${this.saving || this.loading} @click=${this._reset}>${t('messages.settings.reset', 'Standard wiederherstellen')}</button>
          <button class="btn primary" type="button" ?disabled=${this.saving || this.loading} @click=${this._save}>${this.saving ? t('messages.state.saving', 'Wird gespeichert…') : t('messages.action.save', 'Speichern')}</button>
        `}
        @ks-modal-close=${this._close}
      ></ks-modal>
    `;
  }
}

if (!customElements.get('ks-message-settings-admin-modal')) customElements.define('ks-message-settings-admin-modal', KsMessageSettingsAdminModal);

let activeModal = null;

function openMessageSettingsAdminModal() {
  activeModal?.remove();
  activeModal = document.createElement('ks-message-settings-admin-modal');
  activeModal.addEventListener('ks-modal-close', () => { activeModal = null; });
  document.body.appendChild(activeModal);
}

export { openMessageSettingsAdminModal };
